/**
 * Настройки → Отделения (ТЗ §8.7): список, создание и правка.
 *
 * Удаления в контракте нет (`GET/POST/PATCH /api/settings/departments`), и это правильно:
 * отделение стоит в `gms_cases.department_id` и в правилах маршрутизации, удаление порвало бы
 * аналитику по отделениям. Ненужное отделение убирается из маршрутов и уходит вниз списка.
 */

import { useState, type FormEvent } from 'react';
import type { DepartmentDto, DepartmentInput } from '@/api/types';
import { errorMessage, FormError, LoadingState } from '@/components/ErrorState';

export interface DepartmentsTabProps {
    departments: DepartmentDto[];
    loading?: boolean;
    error?: unknown;
    onCreate: (input: DepartmentInput) => Promise<unknown>;
    onUpdate: (id: string, input: DepartmentInput) => Promise<unknown>;
}

type Mode = { kind: 'none' } | { kind: 'create' } | { kind: 'edit'; id: string };

const EMPTY_FORM = { name: '', head_name: '', icon: '', sort: '' };

export default function DepartmentsTab({
    departments,
    loading,
    error,
    onCreate,
    onUpdate,
}: DepartmentsTabProps) {
    const [mode, setMode] = useState<Mode>({ kind: 'none' });
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const openCreate = () => {
        setForm({ ...EMPTY_FORM, sort: String((departments.length + 1) * 10) });
        setFormError(null);
        setMode({ kind: 'create' });
    };

    const openEdit = (department: DepartmentDto) => {
        setForm({
            name: department.name,
            head_name: department.head_name ?? '',
            icon: department.icon ?? '',
            sort: department.sort === null || department.sort === undefined ? '' : String(department.sort),
        });
        setFormError(null);
        setMode({ kind: 'edit', id: department.id });
    };

    const close = () => {
        setMode({ kind: 'none' });
        setFormError(null);
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (busy || mode.kind === 'none') return;

        const name = form.name.trim();
        if (!name) {
            setFormError('Название отделения обязательно.');
            return;
        }
        const sortRaw = form.sort.trim();
        if (sortRaw && !/^-?\d+$/.test(sortRaw)) {
            setFormError('Порядок — целое число.');
            return;
        }

        const input: DepartmentInput = {
            name,
            head_name: form.head_name.trim() || null,
            icon: form.icon.trim() || null,
            sort: sortRaw ? Number(sortRaw) : undefined,
        };

        const current = mode;
        setBusy(true);
        setFormError(null);
        void (async () => {
            try {
                if (current.kind === 'create') await onCreate(input);
                else await onUpdate(current.id, input);
                close();
            } catch (cause) {
                setFormError(errorMessage(cause));
            } finally {
                setBusy(false);
            }
        })();
    };

    return (
        <>
            <div className="filter-bar">
                <span className="checklist-title">🏥 Отделения и подразделения</span>
                <button type="button" className="act-btn primary" onClick={openCreate}>
                    ＋ Новое отделение
                </button>
            </div>

            {mode.kind !== 'none' ? (
                <div className="detail-box">
                    <div className="detail-title">
                        <span>{mode.kind === 'create' ? 'Новое отделение' : 'Изменение отделения'}</span>
                        <span className="period-note">используется в маршрутизации и аналитике</span>
                    </div>

                    <form onSubmit={onSubmit}>
                        <div className="kv">
                            <span className="k">Название</span>
                            <span className="v">
                                <input
                                    type="text"
                                    className="search-input"
                                    size={32}
                                    value={form.name}
                                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                                    aria-label="Название отделения"
                                />
                            </span>

                            <span className="k">Заведующий</span>
                            <span className="v">
                                <input
                                    type="text"
                                    className="search-input"
                                    size={32}
                                    value={form.head_name}
                                    placeholder="Фамилия И.О."
                                    onChange={(event) => setForm({ ...form, head_name: event.target.value })}
                                    aria-label="Заведующий отделением"
                                />
                            </span>

                            <span className="k">Значок</span>
                            <span className="v">
                                <input
                                    type="text"
                                    className="search-input"
                                    size={6}
                                    maxLength={4}
                                    value={form.icon}
                                    placeholder="🦴"
                                    onChange={(event) => setForm({ ...form, icon: event.target.value })}
                                    aria-label="Значок отделения"
                                />
                            </span>

                            <span className="k">Порядок</span>
                            <span className="v">
                                <input
                                    type="text"
                                    className="search-input"
                                    size={6}
                                    value={form.sort}
                                    onChange={(event) => setForm({ ...form, sort: event.target.value })}
                                    aria-label="Порядок сортировки"
                                />
                            </span>
                        </div>

                        <FormError>{formError}</FormError>

                        <div className="draft-bar">
                            <button type="submit" className="act-btn primary" disabled={busy}>
                                {busy ? 'Сохраняем…' : 'Сохранить'}
                            </button>
                            <button type="button" className="act-btn" disabled={busy} onClick={close}>
                                Отмена
                            </button>
                            <span className="draft-note">
                                Порядок задаёт место отделения в блоке «Рост жалоб по отделениям».
                            </span>
                        </div>
                    </form>
                </div>
            ) : null}

            <div className="stat-block">
                <div className="block-header">
                    <span>🏥 Отделения</span>
                    <span className="checklist-progress">{departments.length}</span>
                </div>
                <div className="block-body">
                    <FormError>{error ? errorMessage(error) : null}</FormError>
                    {loading ? <LoadingState variant="inline" label="Загружаем отделения…" /> : null}
                    {!loading && departments.length === 0 ? (
                        <div className="list-empty">Отделения ещё не заведены.</div>
                    ) : null}

                    {departments.map((department) => (
                        <div className="check-item" key={department.id}>
                            <span className="check-src">{department.icon || '🏥'}</span>
                            <span className="check-text">
                                {department.name}
                                <br />
                                <span className="my-appl">
                                    {[
                                        department.head_name
                                            ? `зав. ${department.head_name}`
                                            : 'заведующий не указан',
                                        department.sort === null || department.sort === undefined
                                            ? null
                                            : `порядок ${department.sort}`,
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </span>
                            </span>
                            <span className="review-wrap">
                                <button
                                    type="button"
                                    className="act-btn"
                                    disabled={busy}
                                    onClick={() => openEdit(department)}
                                >
                                    Изменить
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
                <div className="totals-row">
                    <span>
                        Отделение подставляется в дело из правила маршрутизации, а при совпадении названия — из
                        подсказки классификатора.
                    </span>
                </div>
            </div>
        </>
    );
}
